// src/Components/AuthRequired.tsx
// HYBRID AUTH: Works with both password (local) and Microsoft (Azure)

import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import useAuth from '../hooks/useAuth.ts';

const AuthRequired: React.FC = () => {
  const { 
    authenticated, 
    setAuthenticated, 
    setName, 
    setEmail, 
    setRoles, 
    setOrganizationIds 
  } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [userNotProvisioned, setUserNotProvisioned] = useState(false);
  const [provisioningInfo, setProvisioningInfo] = useState<{ email?: string; message?: string }>({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuthentication();
  }, [location.pathname]);

  const checkAuthentication = async () => {
    try {
      // FIRST: Check if we have a JWT token in localStorage
      const token = localStorage.getItem('token');
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        try {
          // Validate the JWT via /api/auth/status
          const { data } = await axios.get(`${backendUrl}/api/auth/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (data.authenticated) {
            setAuthenticated(true);
            setName(localStorage.getItem('name'));
            setEmail(localStorage.getItem('email'));
            setRoles(JSON.parse(localStorage.getItem('roles') || 'null'));
            setOrganizationIds(JSON.parse(localStorage.getItem('organizationIds') || 'null'));
            setChecking(false);
            return;
          }
        } catch {
          // Token invalid, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('isLoggedIn');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      // SECOND: Try to get Easy Auth session (Microsoft auth in Azure)
      // This endpoint checks X-MS-CLIENT-PRINCIPAL header set by Easy Auth
      try {
        const callbackResponse = await axios.get(
          `${backendUrl}/api/auth/microsoft/callback`,
          { withCredentials: true }
        );
        
        if (callbackResponse.data.authenticated) {
          const { username, email, roles, organizationIds, jwt } = callbackResponse.data;
          
          localStorage.setItem('token', jwt);
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('name', username);
          localStorage.setItem('email', email);
          localStorage.setItem('roles', JSON.stringify(roles));
          localStorage.setItem('organizationIds', JSON.stringify(organizationIds));
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
          
          setAuthenticated(true);
          setName(username);
          setEmail(email);
          setRoles(roles);
          setOrganizationIds(organizationIds);
          setChecking(false);
          return;
        }
      } catch (callbackError: any) {
        // Check for "user not provisioned" error
        if (callbackError.response?.status === 403) {
          const data = callbackError.response.data;
          if (data.redirect === '/user-not-provisioned' || 
              (data.message && data.message.includes('not provisioned'))) {
            setUserNotProvisioned(true);
            setProvisioningInfo({ email: data.email, message: data.message });
            setChecking(false);
            return;
          }
        }
        // No Easy Auth session - this is expected locally
        console.log('No Easy Auth session (expected in local dev)');
      }
      
      // No authentication found
      setAuthenticated(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.clear();
      delete axios.defaults.headers.common['Authorization'];
      setAuthenticated(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #0078d4',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>Verifying authentication...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (userNotProvisioned) {
    return <Navigate to="/user-not-provisioned" state={provisioningInfo} replace />;
  }

  if (!authenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export default AuthRequired;